import { useState, useEffect, useCallback, useMemo } from "react";
import { X, Heart, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { tx } from "../lib/tx";

/**
 * Popup de message de motivation pour trouver l'amour
 * Affiché 2 fois par jour à des moments aléatoires
 */
export default function MotivationPopup({ message, onClose, userName }) {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    // Auto-fermeture après 10 secondes
    const timer = setTimeout(() => {
      handleClose();
    }, 10000);

    return () => clearTimeout(timer);
  }, [handleClose]);

  // Générer les positions aléatoires une seule fois avec useMemo
  const heartPositions = useMemo(() => {
    return [...Array(5)].map((_, i) => ({
      initialX: Math.random() * 100 - 50,
      initialY: Math.random() * 100 + 100,
      animateX: Math.random() * 100 - 50,
      duration: 3 + Math.random() * 2,
      left: `${20 + i * 15}%`
    }));
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-gradient-to-br from-pink-600 via-rose-500 to-red-500 rounded-3xl p-6 max-w-sm w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermer */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Icône animée */}
            <div className="flex justify-center mb-4">
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
                className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center"
              >
                <Heart className="w-8 h-8 text-white fill-white" />
              </motion.div>
            </div>

            {/* Titre */}
            <h3 className="text-xl font-bold text-white text-center mb-2 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5" />
              {tx("Message du jour")}
              <Sparkles className="w-5 h-5" />
            </h3>

            {/* Message de motivation */}
            <p className="text-white/95 text-center text-base leading-relaxed mb-6">
              {message}
            </p>

            {/* Bouton d'action */}
            <button
              onClick={handleClose}
              className="w-full py-3 bg-white text-rose-600 font-bold rounded-xl hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
            >
              <Heart className="w-5 h-5" />
              {tx("Merci pour la motivation !")}
            </button>

            {/* Petits cœurs décoratifs */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-3xl">
              {heartPositions.map((pos, i) => (
                <motion.div
                  key={i}
                  className="absolute text-white/20"
                  initial={{ 
                    x: pos.initialX, 
                    y: pos.initialY,
                    opacity: 0 
                  }}
                  animate={{ 
                    y: -100, 
                    opacity: [0, 1, 0],
                    x: pos.animateX
                  }}
                  transition={{ 
                    duration: pos.duration, 
                    repeat: Infinity,
                    delay: i * 0.5
                  }}
                  style={{
                    left: pos.left
                  }}
                >
                  <Heart className="w-4 h-4 fill-current" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
